import React, { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { Avatar } from '@components/Avatar/Avatar'
import { Panel } from '@components/Panel/Panel'
import { SpaceIcon } from '@components/SpaceIcon'
import { ImageVariants } from '@components/UploadImage/useImageSource'
import { Paragraph, Stack } from '@ui'
import { PATHS } from 'routes'
import { usePanelActions } from 'routes/layouts/hooks/usePanelActions'
import { addressFromSpaceId } from 'ui/utils/utils'
import { useMemberOf, useMyUserId, useTownsContext, useUserLookup } from 'use-towns-client'
import { getPrettyDisplayName } from 'utils/getPrettyDisplayName'

export const MutualTownsPanel = (props: { userId: string }) => {
    const { userId } = props

    const { spaces } = useTownsContext()
    const user = useUserLookup(userId)

    const memberOf = useMemberOf(userId)
    const mutualTowns = useMemo(
        () => spaces.filter((s) => (memberOf ? s.id in memberOf : false)),
        [memberOf, spaces],
    )
    const navigate = useNavigate()

    const { openPanel } = usePanelActions()

    const onUserClick = useCallback(() => {
        if (userId) {
            openPanel('profile', { profileId: userId })
        }
    }, [openPanel, userId])

    const onTownClick = useCallback(
        (spaceId: string) => {
            navigate(`/${PATHS.SPACES}/${addressFromSpaceId(spaceId)}`)
        },
        [navigate],
    )

    const isMyProfile = userId === useMyUserId()

    const panelLabel = useMemo(() => {
        if (user?.isBot) {
            return 'Installed In'
        }
        return isMyProfile ? 'Towns' : 'Mutual Towns'
    }, [user?.isBot, isMyProfile])

    return (
        <Panel label={panelLabel}>
            <Stack gap>
                {!isMyProfile && (
                    <Stack horizontal gap="sm" alignItems="center" color="gray2" flexWrap="wrap">
                        <Paragraph>
                            {user?.isBot
                                ? `Installed in ${mutualTowns.length} town${
                                      mutualTowns.length > 1 ? 's' : ''
                                  }`
                                : `You share ${mutualTowns.length} town${
                                      mutualTowns.length > 1 ? 's' : ''
                                  } with`}
                        </Paragraph>
                        {!user?.isBot && (
                            <Stack
                                horizontal
                                hoverable
                                gap="sm"
                                color="gray1"
                                padding="sm"
                                paddingRight="paragraph"
                                borderRadius="full"
                                background="level2"
                                alignItems="center"
                                cursor="pointer"
                                onClick={onUserClick}
                            >
                                <Avatar userId={userId} size="avatar_xs" />
                                <Paragraph whiteSpace="nowrap">
                                    {getPrettyDisplayName(user)}
                                </Paragraph>
                            </Stack>
                        )}
                    </Stack>
                )}
                <Stack inset="xs">
                    {mutualTowns.map((s) => (
                        <Stack
                            horizontal
                            hoverable
                            borderRadius="sm"
                            key={s.id}
                            gap="sm"
                            alignItems="center"
                            background="level1"
                            padding="sm"
                            cursor="pointer"
                            onClick={() => {
                                onTownClick(s.id)
                            }}
                        >
                            <SpaceIcon
                                background="level1"
                                border="strongFaint"
                                rounded="sm"
                                letterFontSize="sm"
                                width="x4"
                                height="x4"
                                spaceId={s?.id}
                                firstLetterOfSpaceName={s?.name[0]}
                                overrideBorderRadius="xs"
                                variant={ImageVariants.thumbnail50}
                                fadeIn={false}
                                tooltip={s?.name}
                            />
                            <Paragraph color="gray1">{s.name}</Paragraph>
                        </Stack>
                    ))}
                </Stack>
            </Stack>
        </Panel>
    )
}
